import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { FileText, Loader2, Printer, Mail } from 'lucide-react'
import ReportEndpoints from '../../endpoints/ReportEndpoints'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'

export interface ReportViewerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  html: string
  signature?: string
  clientId?: number | null
}

/**
 * Full-screen dialog that renders a generated HTML report with print and email
 * actions. Mount at most one per page — the print handler finds the iframe by
 * its title attribute in the global DOM.
 */
export const ReportViewer: React.FC<ReportViewerProps> = ({ open, onOpenChange, title, html, signature, clientId }) => {
  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [showEmail, setShowEmail] = useState(false)

  useEffect(() => {
    if (open) {
      setEmailSubject(title)
      setEmailTo('')
      setEmailMessage('')
      setShowEmail(false)
    }
  }, [open, title])

  const handlePrint = () => {
    const iframe = document.querySelector('iframe[title="Report Preview"]') as HTMLIFrameElement
    if (iframe?.contentWindow) iframe.contentWindow.print()
  }

  const handleEmail = async () => {
    if (!emailTo.trim()) { toast.error('Please enter recipient email(s)'); return }
    setSending(true)
    try {
      await ReportEndpoints.sendReportEmail({
        subject: emailSubject,
        recipients: emailTo.split(',').map((e) => e.trim()),
        content: html,
        customMessage: emailMessage,
        signature: signature || '',
        clientId: clientId ?? null,
      })
      toast.success('Report emailed')
      setShowEmail(false)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to send email'))
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[95vw] w-[95vw] h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {showEmail && (
          <div className="px-6 space-y-3 border-b pb-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">To (comma-separated)</Label>
                <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="email@example.com" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Subject</Label>
                <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message (optional)</Label>
              <Input value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} placeholder="Additional message..." />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowEmail(false)}>Cancel</Button>
              <Button size="sm" onClick={handleEmail} disabled={sending}>
                {sending ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Sending...</> : <><Mail className="mr-2 h-3.5 w-3.5" />Send</>}
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 px-6 pb-2">
          <iframe
            srcDoc={html}
            className="w-full h-full border rounded-lg bg-white"
            title="Report Preview"
            style={{ overflow: 'auto' }}
            scrolling="yes"
          />
        </div>

        <DialogFooter className="px-6 pb-6 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button variant="outline" onClick={() => setShowEmail(!showEmail)}>
            <Mail className="mr-2 h-4 w-4" />Email
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Per-page viewer state: spread `viewerProps` onto a <ReportViewer/>, call
 * `openReport` from a generate handler, and use `generating` for the button state.
 */
export const useReportViewer = () => {
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerTitle, setViewerTitle] = useState('')
  const [viewerHtml, setViewerHtml] = useState('')
  const [viewerSignature, setViewerSignature] = useState('')
  const [viewerClientId, setViewerClientId] = useState<number | null>(null)
  const [generating, setGenerating] = useState(false)

  const openReport = (title: string, res: any, clientId?: number | null) => {
    setViewerTitle(title)
    setViewerHtml(res.data)
    setViewerSignature(res.signature || '')
    setViewerClientId(clientId ?? null)
    setViewerOpen(true)
  }

  const viewerProps: ReportViewerProps = {
    open: viewerOpen,
    onOpenChange: setViewerOpen,
    title: viewerTitle,
    html: viewerHtml,
    signature: viewerSignature,
    clientId: viewerClientId,
  }

  return { viewerProps, openReport, generating, setGenerating }
}
